import React from 'react';

/**
 * A search hit's SNIPPET (KPR-490, KPR-1706; keepr-api docs/SCHEMA.md
 * "Search inside a collection", *The snippet*): a few words of ONE field of
 * the item around the matches, as the server quoted them —
 *
 *   { in: { kind: 'title' | 'element' | 'notes' | 'tag', name? },
 *     parts: [{ text, mark }], before, after }
 *
 * `parts` are plain text, never markup, and they are drawn as React text
 * nodes only: a value holding `<b>`, `<script>` or markdown shows exactly
 * those characters. A marked part is a `<mark>` (the `.search-mark` tint, the
 * one accent's); `before` / `after` say the field goes on, drawn as an
 * ellipsis. Anything that is not that shape — no snippet (an API from before
 * KPR-490, a rollback), null, no parts, a part whose text is not a string —
 * draws nothing for that piece, never an error.
 */

/** Whether `snippet` is a snippet this component can draw (an object with a `parts` array). */
export function isSnippet(snippet) {
	return Boolean(snippet) && typeof snippet === 'object' && Array.isArray(snippet.parts);
}

/** Whether the snippet quotes the item's title, which the row already shows. */
export function isTitleSnippet(snippet) {
	return isSnippet(snippet) && snippet.in?.kind === 'title';
}

/** The parts worth drawing: string text, not empty. */
function drawableParts(snippet) {
	if (!isSnippet(snippet)) return [];
	return snippet.parts.filter(part => part && typeof part.text === 'string' && part.text.length > 0);
}

/** Whether there is anything to draw. */
export function hasSnippetText(snippet) {
	return drawableParts(snippet).some(part => part.text.trim().length > 0);
}

const ELLIPSIS = '…';

export default function SearchSnippet({ snippet, className = '', ...rest }) {
	const parts = drawableParts(snippet);
	if (!parts.some(part => part.text.trim().length > 0)) return null;
	return (
		<span className={`search-snippet${className ? ` ${className}` : ''}`} data-testid='search-snippet' {...rest}>
			{snippet.before ? ELLIPSIS : null}
			{parts.map((part, index) => (
				part.mark === true
					? <mark key={index} className='search-mark'>{part.text}</mark>
					: <React.Fragment key={index}>{part.text}</React.Fragment>
			))}
			{snippet.after ? ELLIPSIS : null}
		</span>
	);
}
